import { useEffect, useRef, useState } from "react";

import AppShell from "../components/AppShell";
import SharingTabs from "../components/SharingTabs";
import { CardSkeleton } from "../components/Skeleton";
import {
  downloadScan,
  getScanJob,
  listScanners,
  startScan,
  type ColorMode,
  type ScannerRow,
} from "../api/scanners";

const REFRESH_MS = 30_000;
const POLL_MS = 2_000;
// The server reports a scan as failed when its computer doesn't pick it up
// within 10 minutes, or doesn't finish within 6 minutes of starting; stop
// polling a little after the longest case.
const POLL_GIVE_UP_MS = 17 * 60_000;

type ScanState =
  | { phase: "scanning" }
  | { phase: "done"; filename: string }
  | { phase: "error"; message: string };

const QUALITY: { label: string; dpi: number }[] = [
  { label: "Normal (good for documents)", dpi: 200 },
  { label: "High (photos, small print)", dpi: 300 },
  { label: "Fast (lower quality)", dpi: 150 },
];

export default function Scanners() {
  const [scanners, setScanners] = useState<ScannerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<Record<string, ScanState>>({});
  const [colorMode, setColorMode] = useState<ColorMode>("color");
  const [dpi, setDpi] = useState(200);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const load = async () => {
      try {
        const rows = await listScanners();
        if (alive.current) {
          setScanners(rows);
          setError(null);
        }
      } catch (e) {
        if (alive.current) setError(extractError(e));
      }
    };
    void load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => {
      alive.current = false;
      window.clearInterval(t);
    };
  }, []);

  function setFor(id: string, s: ScanState) {
    setState((prev) => ({ ...prev, [id]: s }));
  }

  async function onScan(scanner: ScannerRow) {
    setFor(scanner.id, { phase: "scanning" });
    try {
      const { job_id } = await startScan(scanner.id, colorMode, dpi);
      const started = Date.now();
      while (alive.current && Date.now() - started < POLL_GIVE_UP_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        let job;
        try {
          job = await getScanJob(job_id);
        } catch {
          continue; // a dropped poll on office WiFi; try again
        }
        if (job.status === "ready") {
          await downloadScan(job_id, job.filename);
          setFor(scanner.id, { phase: "done", filename: job.filename });
          return;
        }
        if (job.status === "failed") {
          setFor(scanner.id, { phase: "error", message: job.error_message || "The scan didn't work." });
          return;
        }
      }
      if (alive.current) {
        setFor(scanner.id, { phase: "error", message: "The scanner's computer didn't respond. Try again." });
      }
    } catch (e) {
      setFor(scanner.id, { phase: "error", message: extractError(e) });
    }
  }

  const busy = Object.values(state).some((s) => s.phase === "scanning");

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Sharing</h1>
        <p className="mt-1 text-slate-600">Share folders, printers and scanners with everyone in the office.</p>
        <SharingTabs />

        <h2 className="text-lg font-semibold text-slate-900">Scanners</h2>
        <p className="mt-1 text-sm text-slate-600">
          Scanners connected to your office computers. Put the page in the scanner, then click
          &ldquo;Scan&rdquo;. The scanned page downloads to this device.
        </p>

        <div className="mt-4 flex flex-wrap gap-4 rounded-xl border border-slate-200 bg-white p-4">
          <label className="flex flex-col text-sm text-slate-700">
            <span className="mb-1 font-medium">Colors</span>
            <select
              className="rounded-lg border border-slate-300 px-3 py-2"
              value={colorMode}
              onChange={(e) => setColorMode(e.target.value as ColorMode)}
              disabled={busy}
            >
              <option value="color">Color</option>
              <option value="gray">Grayscale</option>
              <option value="bw">Black and white</option>
            </select>
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            <span className="mb-1 font-medium">Quality</span>
            <select
              className="rounded-lg border border-slate-300 px-3 py-2"
              value={dpi}
              onChange={(e) => setDpi(Number(e.target.value))}
              disabled={busy}
            >
              {QUALITY.map((q) => (
                <option key={q.dpi} value={q.dpi}>
                  {q.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        {scanners === null && !error ? (
          <div className="mt-6">
            <CardSkeleton />
          </div>
        ) : null}

        {scanners !== null && scanners.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-600">
            <p className="font-medium text-slate-900">No scanners found yet.</p>
            <p className="mt-1">
              Connect the scanner to one of your office computers and make sure Windows can use it
              (it should appear in Windows Settings &rarr; Printers &amp; scanners). It shows up here
              within about 5 minutes.
            </p>
          </div>
        ) : null}

        <ul className="mt-6 space-y-3">
          {(scanners ?? []).map((s) => {
            const st = state[s.id];
            return (
              <li key={s.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{s.name}</p>
                    <p className="text-sm text-slate-600">
                      On {s.host_name}
                      {s.available ? "" : " — this computer is off or the scanner is unplugged"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onScan(s)}
                    disabled={!s.available || busy}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white [-webkit-tap-highlight-color:transparent] hover:bg-slate-800 disabled:opacity-50"
                  >
                    {st?.phase === "scanning" ? "Scanning…" : "Scan"}
                  </button>
                </div>
                {st?.phase === "scanning" ? (
                  <p className="mt-3 text-sm text-slate-600" role="status">
                    Scanning… this takes up to a minute.
                  </p>
                ) : null}
                {st?.phase === "done" ? (
                  <p className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800" role="status">
                    Scanned! &ldquo;{st.filename}&rdquo; was downloaded to this device.
                  </p>
                ) : null}
                {st?.phase === "error" ? (
                  <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-800" role="alert">
                    {st.message}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </AppShell>
  );
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "Something went wrong. Please try again.";
}
